import { Download, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useDownloadLvdProductComplianceFileOutputDocument,
  type LvdProductComplianceFileOutputDocument,
  type LvdProductComplianceFileOutputDocumentStatus,
} from '@/shared/hooks/api/lvdComplianceFiles'

type LvdProductComplianceFileOutputDocumentsSectionProps = {
  productId: string
  outputDocuments: LvdProductComplianceFileOutputDocument[]
}

function outputDocumentStatusVariant(
  status: LvdProductComplianceFileOutputDocumentStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'COMPLETED') {
    return 'default'
  }
  if (status === 'FAILED') {
    return 'destructive'
  }
  return 'secondary'
}

function formatCreatedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

function OutputDocumentActionsCell({
  productId,
  document,
}: {
  productId: string
  document: LvdProductComplianceFileOutputDocument
}) {
  const { t } = useTranslation('common')
  const downloadMutation = useDownloadLvdProductComplianceFileOutputDocument(
    productId
  )

  const isInFlight =
    document.status === 'PENDING' || document.status === 'PROCESSING'

  if (document.status === 'COMPLETED') {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={downloadMutation.isPending}
        onClick={() => {
          void downloadMutation
            .mutateAsync({
              outputDocumentId: document.id,
              fileName: document.fileName,
            })
            .catch(() => {
              toast.error(
                t(
                  'v2.pages.products.detail.lvdFiles.outputDocuments.downloadFailed'
                )
              )
            })
        }}
      >
        <Download className="size-3.5" />
        {t('v2.pages.products.detail.lvdFiles.outputDocuments.download')}
      </Button>
    )
  }

  if (isInFlight) {
    return (
      <Button type="button" variant="outline" size="sm" disabled>
        <Loader2 className="size-3.5 animate-spin" />
        {t('v2.pages.products.detail.lvdFiles.outputDocuments.generating')}
      </Button>
    )
  }

  return <span className="text-sm text-muted-foreground">—</span>
}

export function LvdProductComplianceFileOutputDocumentsSection({
  productId,
  outputDocuments,
}: LvdProductComplianceFileOutputDocumentsSectionProps) {
  const { t, i18n } = useTranslation('common')

  if (outputDocuments.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.products.detail.lvdFiles.outputDocuments.empty')}
      </p>
    )
  }

  return (
    <div className='overflow-x-auto'>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            {t(
              'v2.pages.products.detail.lvdFiles.outputDocuments.columns.status'
            )}
          </TableHead>
          <TableHead>
            {t(
              'v2.pages.products.detail.lvdFiles.outputDocuments.columns.declarationNumber'
            )}
          </TableHead>
          <TableHead>
            {t(
              'v2.pages.products.detail.lvdFiles.outputDocuments.columns.createdAt'
            )}
          </TableHead>
          <TableHead className="text-right">
            {t(
              'v2.pages.products.detail.lvdFiles.outputDocuments.columns.actions'
            )}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {outputDocuments.map((document) => (
          <TableRow key={document.id}>
            <TableCell>
              <div className="space-y-1">
                <Badge
                  variant={outputDocumentStatusVariant(document.status)}
                  className="inline-flex items-center gap-1.5"
                >
                  {(document.status === 'PENDING' ||
                    document.status === 'PROCESSING') && (
                    <Loader2 className="size-3 animate-spin" />
                  )}
                  {t(
                    `v2.pages.products.detail.lvdFiles.outputDocuments.statuses.${document.status}`
                  )}
                </Badge>
                {document.status === 'FAILED' &&
                  document.errorMessage != null &&
                  document.errorMessage.trim() !== '' && (
                    <p className="text-xs text-destructive">
                      {document.errorMessage}
                    </p>
                  )}
              </div>
            </TableCell>
            <TableCell className="font-mono text-xs">
              {document.declarationNumber ?? '—'}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatCreatedAt(document.createdAt, i18n.language)}
            </TableCell>
            <TableCell className="text-right">
              <OutputDocumentActionsCell
                productId={productId}
                document={document}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </div>
  )
}
